import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wrench, Upload, Check, AlertTriangle, RefreshCw, Database, GitBranch, Users } from 'lucide-react';
import api from '../api/client';
import toast from 'react-hot-toast';

export default function TemplateStudio() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(null);
  const [confirmPublish, setConfirmPublish] = useState(null);
  const [publishNotes, setPublishNotes] = useState('');
  const [publishConfirmed, setPublishConfirmed] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/platform/template-studio');
      setData(res.data);
    } catch {
      toast.error('Failed to load Template Studio');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handlePublish = async (rt) => {
    setPublishing(rt.id);
    try {
      const res = await api.post(`/platform/template-studio/publish/${rt.id}`, {
        notes: publishNotes || null,
      });
      toast.success(res.data.message);
      setConfirmPublish(null);
      setPublishNotes('');
      setPublishConfirmed(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Publish failed');
    } finally {
      setPublishing(null);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-64 bg-gray-200 rounded" />
          <div className="space-y-3">
            {[1,2,3].map(i => <div key={i} className="h-24 bg-gray-200 rounded-xl" />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Wrench className="w-6 h-6 text-purple-500" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Template Studio</h1>
          <p className="text-sm text-gray-500">
            Edit master workflow templates, then publish to the Shop
          </p>
        </div>
      </div>

      {/* Purple banner */}
      <div className="bg-purple-100 border border-purple-200 rounded-xl p-4 mb-6">
        <p className="text-sm text-purple-800">
          <strong>TEMPLATE STUDIO</strong> — You are editing master workflow templates.
          Changes here affect future tenant installs only. Use the tools below to edit
          fields, workflows, and screens, then click "Publish to Shop" to create a
          new version snapshot. Existing tenant installs are <strong>never</strong> affected.
        </p>
      </div>

      {/* Record Types */}
      <div className="space-y-3">
        {data?.record_types?.map(rt => (
          <div key={rt.id} className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="h-11 w-11 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: (rt.color || '#6366F1') + '15' }}>
                  <Wrench className="h-5 w-5" style={{ color: rt.color || '#6366F1' }} />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">{rt.name}</h3>
                  <div className="flex items-center gap-3 mt-0.5">
                    {rt.template_name && (
                      <span className="text-xs text-gray-500">
                        Shop: <span className="font-medium">{rt.template_name}</span>
                        {' '}v{rt.template_version}
                      </span>
                    )}
                    <span className="flex items-center gap-1 text-xs text-gray-400">
                      <Database className="h-3 w-3" /> {rt.field_count || 0} fields
                    </span>
                    <span className="flex items-center gap-1 text-xs text-gray-400">
                      <GitBranch className="h-3 w-3" /> {rt.stage_count || 0} stages
                    </span>
                    {rt.install_count > 0 && (
                      <span className="flex items-center gap-1 text-xs text-gray-400">
                        <Users className="h-3 w-3" /> {rt.install_count} install{rt.install_count !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-2 mt-3 pl-15">
              <button onClick={() => navigate(`/template-studio/${rt.id}/fields`)}
                className="px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                Manage Fields
              </button>
              <button onClick={() => navigate(`/template-studio/${rt.id}/workflow`)}
                className="px-3 py-1.5 text-xs font-medium text-green-600 bg-green-50 rounded-lg hover:bg-green-100 transition-colors">
                Workflow
              </button>
              <button onClick={() => navigate(`/template-studio/${rt.id}/screens`)}
                className="px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors">
                Screens
              </button>
              <button onClick={() => navigate(`/template-studio/${rt.id}/title`)}
                className="px-3 py-1.5 text-xs font-medium text-amber-600 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors">
                Title Formula
              </button>
              <button onClick={() => navigate(`/template-studio/${rt.id}/relationships`)}
                className="px-3 py-1.5 text-xs font-medium text-purple-600 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors">
                Relationships
              </button>
              {rt.source_template_id && (
                <button onClick={() => { setConfirmPublish(rt); setPublishNotes(''); setPublishConfirmed(false); }}
                  disabled={publishing === rt.id}
                  className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors">
                  <Upload className="h-3.5 w-3.5" />
                  {publishing === rt.id ? 'Publishing...' : 'Publish to Shop'}
                </button>
              )}
              {!rt.source_template_id && (
                <span className="ml-auto text-xs text-gray-400">Not linked to Shop</span>
              )}
            </div>
          </div>
        ))}
        {(!data?.record_types || data.record_types.length === 0) && (
          <p className="text-sm text-gray-400 text-center py-8">
            No record types in Template Studio.
          </p>
        )}
      </div>

      {/* Publish Confirmation Modal */}
      {confirmPublish && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setConfirmPublish(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                <Upload className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <h2 className="text-base font-bold text-gray-900">
                  Publish {confirmPublish.name} to Shop
                </h2>
                <p className="text-xs text-gray-500">Create a new version snapshot</p>
              </div>
            </div>

            <div className="space-y-3 mb-5">
              <p className="text-sm text-gray-700">
                This will create <strong>v{(confirmPublish.template_version || 0) + 1}</strong> of{' '}
                "<strong>{confirmPublish.template_name}</strong>".
              </p>
              <p className="text-sm text-gray-500">
                Current version: v{confirmPublish.template_version || 0}
              </p>

              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-700">
                  All existing tenant installs are <strong>NOT</strong> affected — ever.
                  Only new installs will receive v{(confirmPublish.template_version || 0) + 1}.
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  Release Notes (optional)
                </label>
                <textarea value={publishNotes} onChange={e => setPublishNotes(e.target.value)}
                  rows={2} placeholder="What changed in this version..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={publishConfirmed}
                  onChange={e => setPublishConfirmed(e.target.checked)}
                  className="rounded border-gray-300 text-purple-600 focus:ring-purple-500" />
                <span className="text-sm text-gray-700">I confirm this publish is intentional</span>
              </label>
            </div>

            <div className="flex gap-3">
              <button onClick={() => setConfirmPublish(null)}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={() => handlePublish(confirmPublish)}
                disabled={publishing || !publishConfirmed}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50">
                {publishing ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                Publish v{(confirmPublish.template_version || 0) + 1}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
